/* eslint-disable no-redeclare */
import {
  Address,
  buildEnumCSV,
  DateOfBirth,
  Email,
  Name,
  NonEmptyText,
  QualifyingLifeEvent,
  Subsidy,
  TobaccoUse,
  TrueFalse,
  Url,
  USAStateAcronym,
  UsPhone,
  UUID,
  ZipCode,
} from '@hc-frontend/core-types';
import { stringToAge } from '@hc-frontend/core-utils-date';
import { z } from 'zod';

export const QuestionWithThirdPartyFields = z.enum([
  'QUALIFYING_LIFE_EVENT',
  'COVERAGE_DURATION',
  'WHEN_YOU_NEED_MEDICARE_COVERAGE',
  'ARE_YOU_CURRENTLY_ENROLLED_IN_MEDICARE',
  'KIND_OF_MEDICARE_PLAN',
  'GENDER',
  'DATE_OF_BIRTH',
  'FIRST_NAME',
  'LAST_NAME',
  'EMAIL',
  'ZIP_CODE',
  'STATE_ACRONYM',
  'CITY',
  'COUNTY',
  'COUNTY_FIPS',
  'ADDRESS',
  'PHONE_NUMBER',
  'DISCLAIMER',
  'TRUSTED_FORM_URL',
  'LEAD_ID',
  'ASK_FOR_ZIP_CODE',
  'IMMEDIATE_COVERAGE',
  'ESTIMATED_SPENDING',
  'INSURE_WHO',
  'VISIT_PURPOSE',
  'SPOUSE_DATE_OF_BIRTH',
  'SPOUSE_GENDER',
  'SPOUSE_TOBACCO',
  'CHILD_1_DATE_OF_BIRTH',
  'CHILD_1_GENDER',
  'CHILD_1_TOBACCO',
  'CHILD_2_DATE_OF_BIRTH',
  'CHILD_2_GENDER',
  'CHILD_2_TOBACCO',
  'CHILD_3_DATE_OF_BIRTH',
  'CHILD_3_GENDER',
  'CHILD_3_TOBACCO',
  'CHILD_4_DATE_OF_BIRTH',
  'CHILD_4_GENDER',
  'CHILD_4_TOBACCO',
  'HOUSEHOLD_SIZE',
  'HOUSEHOLD_INCOME',
  'TOBACCO',
  'INSURANCE_OPTIONS',
  // 'TCPA_SITE',
  'xxTrustedFormCertUrl',
  'universal_leadid',
  'leadid_tcpa_disclosure',
  'xxTrustedFormToken',
  'xxTrustedFormPingUrl',
  'SENIOR_CHECK',
]);
export type QuestionWithThirdPartyFields = z.infer<
  typeof QuestionWithThirdPartyFields
>;

export const Question = QuestionWithThirdPartyFields.exclude([
  'xxTrustedFormCertUrl',
  'universal_leadid',
  'leadid_tcpa_disclosure',
  'xxTrustedFormToken',
  'xxTrustedFormPingUrl',
]);
export type Question = z.infer<typeof Question>;

export const CoverageDuration = z.enum([
  'A_YEAR_OR_MORE',
  'LESS_THAN_A_YEAR',
  'IM_NOT_SURE',
]);

export type CoverageDuration = z.infer<typeof CoverageDuration>;

const CoverageDurationAnswersObject = {
  COVERAGE_DURATION: CoverageDuration,
};
export const CoverageDurationAnswers = z.object(CoverageDurationAnswersObject);

export type CoverageDurationAnswers = z.infer<typeof CoverageDurationAnswers>;

export const SeniorCheck = z.enum(['O65', 'U65']);

export type SeniorCheck = z.infer<typeof SeniorCheck>;

const SeniorCheckAnswersObject = {
  SENIOR_CHECK: SeniorCheck,
};

export const SeniorCheckAnswers = z.object(SeniorCheckAnswersObject);

export type SeniorCheckAnswers = z.infer<typeof SeniorCheckAnswers>;

const QualifyingLifeEventAnswersObject = {
  QUALIFYING_LIFE_EVENT: QualifyingLifeEvent,
};

export const QualifyingLifeEventAnswers = z.object(
  QualifyingLifeEventAnswersObject,
);

export type QualifyingLifeEventAnswers = z.infer<
  typeof QualifyingLifeEventAnswers
>;

export const WhenYouNeedMedicareCoverage = z.enum([
  'RIGHT_AWAY',
  'NEXT_MONTH_OR_LATER',
  'NOT_SURE',
]);

export type WhenYouNeedMedicareCoverage = z.infer<
  typeof WhenYouNeedMedicareCoverage
>;

const BLANK_VALUE = '[BLANK]';
export const BLANK = z.literal(BLANK_VALUE);

export const WhenYouNeedMedicareCoverageOrBlank = z.union([
  WhenYouNeedMedicareCoverage,
  BLANK,
]);
export type WhenYouNeedMedicareCoverageOrBlank = z.infer<
  typeof WhenYouNeedMedicareCoverageOrBlank
>;

const CoverageUrgencyAnswersObject = {
  WHEN_YOU_NEED_MEDICARE_COVERAGE: WhenYouNeedMedicareCoverageOrBlank,
};

export const CoverageUrgencyAnswers = z.object(CoverageUrgencyAnswersObject);

export type CoverageUrgencyAnswers = z.infer<typeof CoverageUrgencyAnswers>;

const AskForZipCode = TrueFalse;

type AskForZipCode = z.infer<typeof AskForZipCode>;

export const DateOfBirthCategory = z.enum(['OVER_65', 'UNDER_65']);

export type DateOfBirthCategory = z.infer<typeof DateOfBirthCategory>;

export const AreYouCurrentlyEnrolledInMedicare = z.enum([
  'YES',
  'NO',
  'NOT_SURE',
]);

export type AreYouCurrentlyEnrolledInMedicare = z.infer<
  typeof AreYouCurrentlyEnrolledInMedicare
>;

export const AreYouCurrentlyEnrolledInMedicareOrBlank = z.union([
  AreYouCurrentlyEnrolledInMedicare,
  BLANK,
]);
export type AreYouCurrentlyEnrolledInMedicareOrBlank = z.infer<
  typeof AreYouCurrentlyEnrolledInMedicareOrBlank
>;

const EnrolledPartAAndPartBAnswersObject = {
  ARE_YOU_CURRENTLY_ENROLLED_IN_MEDICARE:
    AreYouCurrentlyEnrolledInMedicareOrBlank,
};

export const EnrolledPartAAndPartBAnswers = z.object(
  EnrolledPartAAndPartBAnswersObject,
);

export type EnrolledPartAAndPartBAnswers = z.infer<
  typeof EnrolledPartAAndPartBAnswers
>;

export const KindOfMedicarePlan = z.enum([
  'MEDICARE_ADVANTAGE',
  'MEDICARE_SUPPLEMENT_MEDIGAP',
  'NOT_SURE',
]);
export type KindOfMedicarePlan = z.infer<typeof KindOfMedicarePlan>;
export const KindOfMedicarePlanOrBlank = z.union([KindOfMedicarePlan, BLANK]);
export type KindOfMedicarePlanOrBlank = z.infer<
  typeof KindOfMedicarePlanOrBlank
>;
const KindOfMedicarePlanAnswersObject = {
  KIND_OF_MEDICARE_PLAN: KindOfMedicarePlanOrBlank,
};
export const KindOfMedicarePlanAnswers = z.object(
  KindOfMedicarePlanAnswersObject,
);
export type KindOfMedicarePlanAnswers = z.infer<
  typeof KindOfMedicarePlanAnswers
>;

export const InsureWho = z.enum(['JUST_ME', 'MYSELF_AND_OTHERS']);
export type InsureWho = z.infer<typeof InsureWho>;

const InsureWhoAnswersObject = {
  INSURE_WHO: InsureWho,
};
export const InsureWhoAnswers = z.object(InsureWhoAnswersObject);
export type InsureWhoAnswers = z.infer<typeof InsureWhoAnswers>;

export const VisitPurpose = z.enum([
  'ELIGIBILITY_CHECK',
  'CURRENT_PLAN_ASSISTANCE',
  'SWITCH_PLAN',
  'FIRST_TIME_ENROLLMENT',
  'OTHER',
]);
export type VisitPurpose = z.infer<typeof VisitPurpose>;

const VisitPurposeAnswersObject = {
  VISIT_PURPOSE: VisitPurpose,
};
export const VisitPurposeAnswers = z.object(VisitPurposeAnswersObject);
export type VisitPurposeAnswers = z.infer<typeof VisitPurposeAnswers>;

export const EstimatedSpending = z.enum(['LOW', 'MEDIUM', 'HIGH']);
export type EstimatedSpending = z.infer<typeof EstimatedSpending>;
const EstimatedSpendingAnswersObject = {
  ESTIMATED_SPENDING: EstimatedSpending,
};
export const EstimatedSpendingAnswers = z.object(
  EstimatedSpendingAnswersObject,
);
export type EstimatedSpendingAnswers = z.infer<typeof EstimatedSpendingAnswers>;

export const ImmediateCoverage = z.enum(['IMMEDIATELY', '2_WEEKS', 'NOT_SURE']);
export type ImmediateCoverage = z.infer<typeof ImmediateCoverage>;
export const ImmediateCoverageOrBlank = z.union([ImmediateCoverage, BLANK]);
export type ImmediateCoverageOrBlank = z.infer<typeof ImmediateCoverageOrBlank>;

const ImmediateCoverageAnswersObject = {
  IMMEDIATE_COVERAGE: ImmediateCoverageOrBlank,
};

export const ImmediateCoverageAnswers = z.object(
  ImmediateCoverageAnswersObject,
);
export type ImmediateCoverageAnswers = z.infer<typeof ImmediateCoverageAnswers>;

export const InsuranceOptions = z.enum([
  'TERM',
  'PERMANENT',
  'FINAL_EXPENSE',
  'NOT_SURE',
]);
export type InsuranceOptions = z.infer<typeof InsuranceOptions>;

export const InsuranceOptionsCSV = buildEnumCSV(InsuranceOptions, BLANK_VALUE);
export type InsuranceOptionsCSV = z.infer<typeof InsuranceOptionsCSV>;

const InsuranceOptionsAnswersObject = {
  INSURANCE_OPTIONS: InsuranceOptionsCSV,
};

export const InsuranceOptionsAnswers = z.object(InsuranceOptionsAnswersObject);
export type InsuranceOptionsAnswers = z.infer<typeof InsuranceOptionsAnswers>;

const ContextAnswersObject = {
  ASK_FOR_ZIP_CODE: AskForZipCode,
  DATE_OF_BIRTH_CATEGORY: DateOfBirthCategory,
  IS_PHOENIX: TrueFalse,
  IS_OEP: TrueFalse,
  INSURE_WHO: InsureWho,
  ESTIMATED_SPENDING: EstimatedSpending,
};

export const ContextAnswers = z.object(ContextAnswersObject);

export type ContextAnswers = z.infer<typeof ContextAnswers>;

export const GenderType = z.enum(['MALE', 'FEMALE']);

export type GenderType = z.infer<typeof GenderType>;

export const GenderTypeOrBlank = z.union([GenderType, BLANK]);
export type GenderTypeOrBlank = z.infer<typeof GenderTypeOrBlank>;

export const DateOrBlank = z.union([DateOfBirth, BLANK]);
export type DateOrBlank = z.infer<typeof DateOrBlank>;

const GenderAndDateOfBirthAnswersObject = {
  GENDER: GenderTypeOrBlank,
  DATE_OF_BIRTH: DateOrBlank,
};

export const GenderAndDateOfBirthAnswers = z.object(
  GenderAndDateOfBirthAnswersObject,
);

export type GenderAndDateOfBirthAnswers = z.infer<
  typeof GenderAndDateOfBirthAnswers
>;

export const DateOfBirthSpouse = z
  .string()
  .refine((date) => stringToAge(date) >= 18, {
    message: 'Spouse must be 18 years old or older.',
  });
export type DateOfBirthSpouse = z.infer<typeof DateOfBirthSpouse>;

export const DateOfBirthChild = z
  .string()
  .refine((date) => stringToAge(date) <= 25, {
    message: 'Child must be under 26 years of age.',
  });
export type DateOfBirthChild = z.infer<typeof DateOfBirthChild>;

export const FamilyMemberOptions = z.enum([
  'SPOUSE',
  'CHILD_1',
  'CHILD_2',
  'CHILD_3',
  'CHILD_4',
]);

export type FamilyMemberOptions = z.infer<typeof FamilyMemberOptions>;

export const FamilyMember = z.array(FamilyMemberOptions).nullish();

export type FamilyMember = z.infer<typeof FamilyMember>;

const SpouseChildrenAnswersObject = {
  SPOUSE_DATE_OF_BIRTH: DateOfBirthSpouse.nullish(),
  SPOUSE_GENDER: GenderType.nullish(),
  SPOUSE_TOBACCO: TobaccoUse.nullish(),
  CHILD_1_DATE_OF_BIRTH: DateOfBirthChild.nullish(),
  CHILD_1_GENDER: GenderType.nullish(),
  CHILD_1_TOBACCO: TobaccoUse.nullish(),
  CHILD_2_DATE_OF_BIRTH: DateOfBirthChild.nullish(),
  CHILD_2_GENDER: GenderType.nullish(),
  CHILD_2_TOBACCO: TobaccoUse.nullish(),
  CHILD_3_DATE_OF_BIRTH: DateOfBirthChild.nullish(),
  CHILD_3_GENDER: GenderType.nullish(),
  CHILD_3_TOBACCO: TobaccoUse.nullish(),
  CHILD_4_DATE_OF_BIRTH: DateOfBirthChild.nullish(),
  CHILD_4_GENDER: GenderType.nullish(),
  CHILD_4_TOBACCO: TobaccoUse.nullish(),
};

export const SpouseChildrenAnswers = z
  .object(SpouseChildrenAnswersObject)
  .partial();

export type SpouseChildrenAnswers = z.infer<typeof SpouseChildrenAnswers>;

const PersonalInformationAnswersObject = {
  FIRST_NAME: Name,
  LAST_NAME: Name,
  EMAIL: Email,
  ADDRESS: Address.optional(),
  PHONE_NUMBER: UsPhone,
  DISCLAIMER: NonEmptyText,
  TRUSTED_FORM_URL: Url, // TODO: Should this be part of the adapter instead?
  LEAD_ID: UUID,
};

export const PersonalInformationAnswers = z.object(
  PersonalInformationAnswersObject,
);

export type PersonalInformationAnswers = z.infer<
  typeof PersonalInformationAnswers
>;

const LocationAnswersObject = {
  ZIP_CODE: ZipCode,
  STATE_ACRONYM: USAStateAcronym,
  CITY: NonEmptyText,
  COUNTY: NonEmptyText,
  COUNTY_FIPS: NonEmptyText,
};

export const LocationAnswers = z.object(LocationAnswersObject);

export type LocationAnswers = z.infer<typeof LocationAnswers>;

const TobaccoAnswersObject = {
  TOBACCO: TobaccoUse,
};

export const TobaccoAnswers = z.object(TobaccoAnswersObject);
export type TobaccoAnswers = z.infer<typeof TobaccoAnswers>;

export const HouseholdIncome = z.enum(['BELOW', 'BETWEEN', 'OVER']);
export type HouseholdIncome = z.infer<typeof HouseholdIncome>;

const HouseholdIncomeAnswersObject = {
  HOUSEHOLD_INCOME: HouseholdIncome,
};

export const HouseholdIncomeAnswers = z.object(HouseholdIncomeAnswersObject);
export type HouseholdIncomeAnswers = z.infer<typeof HouseholdIncomeAnswers>;

// export const SubsidyCheck = z.number().positive();
// export type SubsidyCheck = z.infer<typeof SubsidyCheck>;

const SubsidyCheckAnswersObject = {
  SUBSIDY_CHECK: Subsidy,
};

export const SubsidyCheckAnswers = z.object(SubsidyCheckAnswersObject);
export type SubsidyCheckAnswers = z.infer<typeof SubsidyCheckAnswers>;

export const SurveyAnswersObject = {
  ...CoverageUrgencyAnswersObject,
  ...EnrolledPartAAndPartBAnswersObject,
  ...KindOfMedicarePlanAnswersObject,
  ...ImmediateCoverageAnswersObject,
  ...GenderAndDateOfBirthAnswersObject,
  ...ContextAnswersObject,
  ...PersonalInformationAnswersObject,
  ...LocationAnswersObject,
  ...CoverageDurationAnswersObject,
  ...EstimatedSpendingAnswersObject,
  ...InsureWhoAnswersObject,
  ...SeniorCheckAnswersObject,
  ...QualifyingLifeEventAnswersObject,
  ...SpouseChildrenAnswersObject,
  ...HouseholdIncomeAnswersObject,
  ...TobaccoAnswersObject,
  ...SubsidyCheckAnswersObject,
  ...VisitPurposeAnswersObject,
  ...InsuranceOptionsAnswersObject,
};

export const SurveyAnswers = z.object(SurveyAnswersObject);

export type SurveyAnswers = z.infer<typeof SurveyAnswers>;

export const PartialSurveyAnswers = SurveyAnswers.partial();

export type PartialSurveyAnswers = z.infer<typeof PartialSurveyAnswers>;
